import { useCallback, useEffect, useState } from 'react';
import { DemostackLogo } from './components/DemostackLogo';
import { DiagnosticResults } from './components/DiagnosticResults';
import { ErrorState } from './components/ErrorState';
import { Methodology } from './components/Methodology';
import { RunningState, type TestPhase } from './components/RunningState';
import { useDiagnosticTest } from './hooks/useDiagnosticTest';

type AppMode = 'test' | 'methodology';

function App() {
  console.log('[App] Component rendering');
  const [appMode, setAppMode] = useState<AppMode>('test');
  const [testPhase, setTestPhase] = useState<TestPhase>('idle');
  const [error, setError] = useState<Error | null>(null);

  console.log('[App] State:', {
    appMode,
    testPhase,
    hasError: !!error,
  });

  const {
    status: diagStatus,
    progress: diagProgress,
    result: diagResult,
    error: diagError,
    startDiagnostic,
    reset: resetDiagnostic,
  } = useDiagnosticTest();

  // Run diagnostic tests only
  const runFullTest = useCallback(async () => {
    console.log('[App] Starting diagnostic tests');
    setTestPhase('diagnostic');
    setError(null);
    resetDiagnostic();

    try {
      await startDiagnostic();
      console.log('[App] Diagnostic tests completed');
      setTestPhase('completed');
    } catch (err) {
      console.error('[App] Test failed with error:', err);
      const testError = err instanceof Error ? err : new Error('Unknown error');
      setError(testError);
      setTestPhase('error');
    }
  }, [startDiagnostic, resetDiagnostic]);

  // Auto-start tests on mount
  useEffect(() => {
    runFullTest();
  }, []);

  const handleRunAgain = useCallback(() => {
    runFullTest();
  }, [runFullTest]);

  // Methodology view
  if (appMode === 'methodology') {
    return <Methodology onClose={() => setAppMode('test')} />;
  }

  const isRunning = testPhase === 'diagnostic';
  const isCompleted = testPhase === 'completed' && diagResult;
  const isError = testPhase === 'error' || diagStatus === 'error';
  const actualError = error || diagError;

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-ds-dark to-ds-dark-lighter text-white">
      {/* Header */}
      <header className="p-4 sm:p-6">
        <div className="max-w-[720px] mx-auto flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl sm:text-2xl font-bold">
                <span className="text-ds-primary">fast</span>
                <span className="text-gray-400">.demo.link</span>
              </h1>
              <span className="px-2 py-0.5 text-xs font-medium bg-ds-accent/20 text-ds-accent rounded">
                Diagnostic
              </span>
            </div>
            <div className="flex items-center gap-1.5 mt-1">
              <span className="text-xs text-gray-500">powered by</span>
              <DemostackLogo className="h-3 text-gray-400" />
            </div>
          </div>
          <button
            type="button"
            onClick={() => setAppMode('methodology')}
            className="text-xs sm:text-sm text-gray-400 hover:text-white transition-colors"
          >
            How we measure
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-start p-4 sm:p-6">
        {isRunning && (
          <RunningState
            phase={testPhase}
            bandwidthProgress={null}
            diagProgress={diagProgress}
            currentSpeed={0}
          />
        )}

        {isCompleted && diagResult && (
          <DiagnosticResults
            result={diagResult}
            bandwidthResult={null}
            onRunAgain={handleRunAgain}
          />
        )}

        {isError && (
          <ErrorState
            error={actualError}
            onRetry={handleRunAgain}
          />
        )}
      </main>

      {/* Footer */}
      <footer className="p-4 sm:p-6 text-center text-xs sm:text-sm text-gray-500">
        <p>Network Performance Diagnostic Tool</p>
        <div className="mt-1 flex items-center justify-center gap-1">
          <span>Powered by</span>
          <DemostackLogo className="h-3 text-gray-400" />
        </div>
      </footer>
    </div>
  );
}

export default App;
